/* assets/race-search.js — standalone Race Search section for Medal Monday
 * Club (sections/race-search.liquid). Created 13 July 2026.
 *
 * A single search field with the Race Finder's autocomplete, but different
 * click behavior:
 *   - suggestion pick  → straight to that product page (/products/<handle>);
 *     Dark/Light editions stay SEPARATE suggestions here (the finder bundles
 *     them) — each entry links to its own product.
 *   - "Show all …" / form submit → the Race Finder page with the query in
 *     ?rf_q=<query>; race-finder.js applies it on load (grid view).
 *
 * Index loading + cache and the dropdown mechanics come from
 * assets/race-search-core.js (shared with race-finder.js). Configure the
 * SAME collections + cache version in both sections and they share one
 * sessionStorage index cache in either direction.
 */
(function () {
  'use strict';

  // Theme editor re-executes section scripts on re-render; second run = no-op.
  if (window.__mmcRaceSearchSectionLoaded) return;
  window.__mmcRaceSearchSectionLoaded = true;

  if (!window.MMCRaceSearch) {
    // eslint-disable-next-line no-console
    console.error('[race-search] race-search-core.js must be loaded first');
    return;
  }
  const {
    raceBaseTitle,
    variantLabel,
    matchesQuery,
    sortCollections,
    fallbackAcItems,
    IndexLoader,
    Autocomplete,
    createLogoAlign,
  } = window.MMCRaceSearch;

  const instances = new Map();

  class RaceSearch {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-rfs-config]').textContent);
      this.strings = this.config.strings || {};
      this.labels = this.config.labels || { distance: {} };
      this.collections = sortCollections(
        (this.config.collections || []).filter((c) => c.handle)
      );
      this.numberFormat = new Intl.NumberFormat('en-US');

      this.sortedRecords = [];
      this.sortDirty = false;
      this.loader = new IndexLoader({
        collections: this.collections,
        dataVersion: this.config.dataVersion,
        onNewRecord: () => {
          this.sortDirty = true;
        },
      });

      this.els = {
        q: root.querySelector('[data-rfs-q]'),
        ac: root.querySelector('[data-rfs-ac]'),
        clear: root.querySelector('[data-rfs-clear]'),
        form: root.querySelector('[data-rfs-form]'),
      };

      this.ac = new Autocomplete({
        input: this.els.q,
        container: this.els.ac,
        scope: this.els.q.closest('.rf__field--search'),
        enabled: Boolean(this.config.autocomplete),
        idPrefix: 'rfs-opt-' + (this.config.sectionId || 'x') + '-',
        strings: {
          empty: this.strings.ac_empty,
          allOne: this.strings.ac_all_one,
          allMany: this.strings.ac_all,
          fallbackLabel: this.strings.ac_fallback,
        },
        formatNumber: (n) => this.numberFormat.format(n),
        canOpen: () => {
          const finder = this.finderInstance();
          return finder ? finder.loadStarted : this.loader.loadStarted;
        },
        onType: () => this.startLoad(),
        onClear: () => {
          // Finder page: clearing the bar resets search AND all dropdown
          // filters (acts like the finder's "Reset all").
          this.resetFinder();
        },
        getItems: (raw) => this.getAcItems(raw),
        onPick: (pick) => this.pick(pick),
      });

      // The index only feeds the suggestions, so without autocomplete there
      // is nothing to prefetch — submit navigates to the finder page anyway.
      if (this.config.autocomplete) {
        const field = this.els.q.closest('.rf__field--search') || this.els.q;
        const prefetch = () => this.startLoad();
        // Hover/focus signal intent before the first keystroke — by the time
        // the visitor types, the index (or its session cache) is on its way.
        field.addEventListener('pointerenter', prefetch, { once: true });
        field.addEventListener('focusin', prefetch, { once: true });
      }

      this.els.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.gotoFinder(this.els.q.value.trim());
      });

      if (this.els.clear) {
        this.els.clear.addEventListener('click', () => {
          this.els.q.value = '';
          this.ac.close();
          this.resetFinder();
          this.els.q.focus();
        });
      }

      if (this.config.autoAlign) {
        this.logoAlign = createLogoAlign({
          root: this.root,
          selector: this.config.logoSelector,
        });
      }

      this.setupStickyTop();
      this.setupPlaceholderRotation();

      // The finder clears its search in several ways the bar cannot see
      // (switching to map view, Reset all, removing the search chip) — it
      // announces every clear, and the bar empties its field along.
      document.addEventListener('rf:search-cleared', () => {
        if (this.els.q.value) {
          this.els.q.value = '';
          this.ac.close();
        }
      });

      // Deep link (?rf_q= from another page): mirror the term into the bar
      // field so the visible search state matches the finder, which applies
      // the query itself on load. Same parsing as race-finder.js.
      const deepLink = /[?&]rf_q=([^&]*)/.exec(window.location.search);
      if (deepLink) {
        const q = decodeURIComponent(deepLink[1].replace(/\+/g, ' ')).trim();
        if (q) this.els.q.value = q;
      }

      root.raceSearch = this; // debugging/support handle
    }

    /* Rotates the resting placeholder terms (config.rotateTerms). Only the
       display span changes — the accessible name, the floated mini label
       and the autocomplete stay untouched. CSS hides the rotating span the
       moment the label floats (focus or value), so the interval can simply
       keep running. */
    setupPlaceholderRotation() {
      const terms = this.config.rotateTerms || [];
      const el = this.root.querySelector('[data-rfs-rotate]');
      if (!el || terms.length < 2) return;
      let index = 0;
      const seconds = parseFloat(this.config.rotateInterval);
      const intervalMs = Math.max(1500, (Number.isFinite(seconds) ? seconds : 3) * 1000);
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.rotateTimer = window.setInterval(() => {
        index = (index + 1) % terms.length;
        if (reduced.matches) {
          el.textContent = terms[index];
          return;
        }
        el.classList.add('is-swapping');
        window.setTimeout(() => {
          el.textContent = terms[index];
          el.classList.remove('is-swapping');
        }, 280);
      }, intervalMs);
    }

    /* On the finder page the finder's own loader does the work (same
       sessionStorage cache); everywhere else this section loads for itself. */
    startLoad() {
      const finder = this.finderInstance();
      if (finder) finder.startLoad();
      else this.loader.start();
    }

    /* The Race Finder instance on the current page, if any — resolved
       lazily at interaction time (the header bar initializes before the
       finder section further down the page). */
    finderInstance() {
      const root = document.querySelector('[data-rf-root]');
      return (root && root.raceFinder) || null;
    }

    /* Full reset of the on-page finder — mirrors its "Reset all" button:
       query AND all dropdown filters (Philipp: deleting the search term on
       the finder page should clear the filters too). */
    resetFinder() {
      const finder = this.finderInstance();
      if (!finder) return;
      if (finder.els && finder.els.q) finder.els.q.value = '';
      finder.setState({ q: '', racetype: null, region: null, country: null });
    }

    /* Sticky bar: pin the section EXACTLY below the theme header by
       measuring its real height at runtime (a static top offset drifts
       whenever the header height changes — e.g. the trimmed bottom
       padding — and sticky then pushes the bar DOWN over the content).
       The Liquid sticky_top value stays as the no-JS fallback. */
    setupStickyTop() {
      if (!this.config.stickyBar) return;
      const section = this.root.closest('.shopify-section');
      const header = document.querySelector('.shopify-section-header');
      if (!section || !header) return;
      let timer = null;
      const apply = () => {
        section.style.top = Math.round(header.getBoundingClientRect().height) + 'px';
      };
      apply();
      // header height settles after fonts/logo load and changes on resize
      window.addEventListener('load', apply);
      window.addEventListener('resize', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(apply, 120);
      });
    }

    ensureSorted() {
      if (!this.sortDirty) return;
      this.sortedRecords = Array.from(this.loader.records.values()).sort((a, b) =>
        a.t.localeCompare(b.t, 'en')
      );
      this.sortDirty = false;
    }

    distanceLabel(key) {
      return this.labels.distance[key] || key;
    }

    getAcItems(raw) {
      // Finder page: delegate to the finder's own suggestion builder — the
      // bar then behaves EXACTLY like the original field (Dark/Light
      // bundled, active dropdown filters respected, picks apply as grid
      // searches instead of navigating).
      const finder = this.finderInstance();
      if (finder && typeof finder.getAcItems === 'function') {
        return finder.getAcItems(raw);
      }
      this.ensureSorted();
      const matches = this.sortedRecords.filter((record) =>
        matchesQuery(record, raw, (key) => this.distanceLabel(key))
      );
      const items = matches.slice(0, 8).map((record) => {
        // Dark/Light stay separate entries; the edition shows in the sub line
        // so the two rows of one race are distinguishable at a glance.
        const variant = variantLabel(record.t, 0);
        const edition =
          variant === 'Dark' || variant === 'Light' ? variant + ' Edition' : null;
        return {
          img: record.i || null,
          title: raceBaseTitle(record.t),
          sub: [edition, record.c, record.d ? this.distanceLabel(record.d) : null]
            .filter(Boolean)
            .join(' · '),
          pick: { handle: record.h },
        };
      });
      return {
        items,
        total: matches.length,
        allPick: { q: raw },
        // Zero matches: offer the configured fallback products instead.
        fallback: matches.length === 0 ? fallbackAcItems(this.config) : null,
      };
    }

    pick(pick) {
      // Finder-mode picks are plain query strings (from finder.getAcItems).
      if (typeof pick === 'string') {
        this.applyToFinder(pick);
        return;
      }
      // Fallback products carry a full URL (also arrives via the delegated
      // finder dropdown on the finder page).
      if (pick && pick.url) {
        this.navigateTo(pick.url);
        return;
      }
      if (pick && pick.handle) {
        const routesRoot =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        this.navigateTo(routesRoot + 'products/' + pick.handle);
        return;
      }
      this.gotoFinder(pick && pick.q ? pick.q : '');
    }

    gotoFinder(q) {
      // Finder on the same page: apply directly instead of reloading.
      if (this.finderInstance()) {
        this.applyToFinder(q);
        return;
      }
      const base = this.config.finderUrl || '/';
      const url = q
        ? base + (base.indexOf('?') === -1 ? '?' : '&') + 'rf_q=' + encodeURIComponent(q)
        : base;
      this.navigateTo(url);
    }

    /* Apply a query to the on-page finder — the original field's behavior:
       term stays visible (here: in the bar), grid view activates, and the
       page scrolls so the finder starts right below the sticky stack. */
    applyToFinder(q) {
      const finder = this.finderInstance();
      if (!finder) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      finder.startLoad();
      finder.applySearch(q);
      this.els.q.value = q;
      this.ac.close();
      this.ac.suppress(500);
      this.els.q.blur();
      const finderRoot = document.querySelector('[data-rf-root]');
      const barSection = this.root.closest('.shopify-section');
      // account for the sticky header + bar covering the viewport top
      const stickyBottom = barSection ? barSection.getBoundingClientRect().height +
        parseFloat(getComputedStyle(barSection).top || '0') : 0;
      const targetY = finderRoot.getBoundingClientRect().top + window.scrollY - stickyBottom;
      window.scrollTo({ top: Math.max(targetY, 0), behavior: reduced ? 'auto' : 'smooth' });
    }

    // Separate method so the harness can stub navigation in tests.
    navigateTo(url) {
      window.location.assign(url);
    }

    destroy() {
      this.ac.destroy();
      if (this.rotateTimer) window.clearInterval(this.rotateTimer);
      if (this.logoAlign) this.logoAlign.destroy();
    }
  }

  /* ---------- bootstrapping (incl. theme editor) ---------- */

  function init(root) {
    if (!root || instances.has(root)) return;
    try {
      instances.set(root, new RaceSearch(root));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[race-search] init failed:', e);
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-rfs-root]').forEach(init);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-rfs-root]').forEach((root) => {
      const instance = instances.get(root);
      if (instance) {
        instance.destroy();
        instances.delete(root);
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
})();
